/**
 * FlatCalc - Flat Roofing Estimator
 * 5-Tab workflow: Spec | Areas | Materials | Labour | Summary
 * Now works with Areas within Projects
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';

// Components
import { TabBar, Button } from '../ui/FormElements';
import SpecTab from './SpecTab';
import AreasTab from './AreasTab';
import MaterialsTab from './MaterialsTab';
import LabourTab from './LabourTab';
import SummaryTab from './SummaryTab';

// Utils & Constants
import { calculateTotalArea } from '../../utils/calculations';
import {
  TABS,
  DEFAULT_SPEC,
  DEFAULT_AREAS,
  DEFAULT_SUMMARY,
  DEFAULT_RUN_CALC_DATA,
  DEFAULT_WASTAGE,
  DEFAULT_LABOUR_RATES
} from '../../constants/flatCalc';

const FlatCalc = () => {
  const { projectId, areaId } = useParams();
  const api = useApi();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('spec');
  
  // Form state
  const [areas, setAreas] = useState(DEFAULT_AREAS);
  const [spec, setSpec] = useState(DEFAULT_SPEC);
  const [materials, setMaterials] = useState([]);
  const [labour, setLabour] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [runCalcData, setRunCalcData] = useState(DEFAULT_RUN_CALC_DATA);
  
  // Data state
  const [manufacturers, setManufacturers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Project/Area state
  const [project, setProject] = useState(null);
  const [area, setArea] = useState(null);
  const [saveStatus, setSaveStatus] = useState('saved');
  const saveTimeoutRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  const isMountedRef = useRef(true);
  const pendingSaveRef = useRef(null);

  // Load data
  useEffect(() => {
    let cancelled = false;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [mfrs, prods, projectData, areaData] = await Promise.all([
          api.getManufacturers(),
          api.getProducts(),
          api.getProject(projectId),
          api.getArea(projectId, areaId)
        ]);
        
        if (cancelled) return;
        
        setManufacturers(mfrs);
        setProducts(prods);
        setProject(projectData.project);
        setArea(areaData);
        
        // Load area data
        if (areaData.spec_data && Object.keys(areaData.spec_data).length > 0) {
          setSpec(areaData.spec_data);
        }
        if (areaData.areas_data && (areaData.areas_data.areas?.length > 0 || areaData.areas_data.upstands?.length > 0)) {
          setAreas(areaData.areas_data);
        }
        if (areaData.materials_data && Array.isArray(areaData.materials_data) && areaData.materials_data.length > 0) {
          setMaterials(areaData.materials_data);
        }
        if (areaData.labour_data && Array.isArray(areaData.labour_data) && areaData.labour_data.length > 0) {
          setLabour(areaData.labour_data);
        }
        if (areaData.summary_data && Object.keys(areaData.summary_data).length > 0) {
          setSummary(areaData.summary_data);
        }
        if (areaData.run_calc_data && Object.keys(areaData.run_calc_data).length > 0) {
          setRunCalcData(areaData.run_calc_data);
        }
        
        // Store initial data for comparison
        lastSavedDataRef.current = JSON.stringify({
          spec: areaData.spec_data,
          areas: areaData.areas_data,
          materials: areaData.materials_data,
          labour: areaData.labour_data,
          summary: areaData.summary_data,
          runCalcData: areaData.run_calc_data
        });
        
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch data:', err);
          setError('Failed to load data. Please refresh.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    
    if (projectId && areaId) {
      fetchData();
    }
    
    return () => {
      cancelled = true;
    };
  }, [projectId, areaId]);

  // Calculate totals
  const totalArea = calculateTotalArea(areas.areas);

  // Build spec summary for display
  const specSummary = useMemo(() => {
    const mfr = manufacturers.find(m => m.id === parseInt(spec.manufacturerId));
    const parts = [];
    if (mfr) parts.push(mfr.name);
    if (spec.membraneType === 'single_ply') parts.push('Single Ply');
    if (spec.membraneType === 'rbm') parts.push('RBM');
    if (spec.buildUp === 'warm') parts.push('Warm Roof');
    if (spec.buildUp === 'cold') parts.push('Cold Roof');
    return parts.join(' ') || null;
  }, [spec, manufacturers]);

  const materialsTotal = useMemo(() => {
    const membrane = spec.membraneType === 'single_ply' 
      ? products.find(p => p.id === parseInt(spec.deckMembraneId)) 
      : null;
    const deckUnderlay = spec.membraneType === 'rbm' 
      ? products.find(p => p.id === parseInt(spec.deckUnderlayId)) 
      : null;
    const deckCap = spec.membraneType === 'rbm' 
      ? products.find(p => p.id === parseInt(spec.deckCapId)) 
      : null;
    const vcl = products.find(p => p.id === parseInt(spec.vclId));
    const insulation = products.find(p => p.id === parseInt(spec.insulationId));
    
    let total = 0;
    
    if (membrane) {
      const coverage = membrane.coverage_m2 || 40;
      const calculated = totalArea / coverage;
      const existing = materials.find(m => m.productId === membrane.id);
      total += Math.ceil(calculated * (1 + (existing?.wastage ?? DEFAULT_WASTAGE.membrane) / 100)) * 
               (existing?.price ?? (membrane.rrp_per_unit || 0));
    }
    
    if (deckUnderlay) {
      const coverage = deckUnderlay.coverage_m2 || 10;
      const calculated = totalArea / coverage;
      const existing = materials.find(m => m.productId === deckUnderlay.id);
      total += Math.ceil(calculated * (1 + (existing?.wastage ?? DEFAULT_WASTAGE.membrane) / 100)) * 
               (existing?.price ?? (deckUnderlay.rrp_per_unit || 0));
    }
    
    if (deckCap) {
      const coverage = deckCap.coverage_m2 || 10;
      const calculated = totalArea / coverage;
      const existing = materials.find(m => m.productId === deckCap.id);
      total += Math.ceil(calculated * (1 + (existing?.wastage ?? DEFAULT_WASTAGE.membrane) / 100)) * 
               (existing?.price ?? (deckCap.rrp_per_unit || 0));
    }
    
    if (vcl) {
      const coverage = vcl.coverage_m2 || 27;
      const calculated = totalArea / coverage;
      const existing = materials.find(m => m.productId === vcl.id);
      total += Math.ceil(calculated * (1 + (existing?.wastage ?? DEFAULT_WASTAGE.vcl) / 100)) * 
               (existing?.price ?? (vcl.rrp_per_unit || 0));
    }
    
    if (insulation) {
      const coverage = insulation.coverage_m2 || 2.88;
      const calculated = (totalArea / coverage) * (spec.insulationLayers || 1);
      const existing = materials.find(m => m.productId === insulation.id);
      total += Math.ceil(calculated * (1 + (existing?.wastage ?? DEFAULT_WASTAGE.insulation) / 100)) * 
               (existing?.price ?? (insulation.rrp_per_unit || 0));
    }
    
    // Add extras
    const extras = materials.filter(m => m.isExtra);
    extras.forEach(e => {
      total += (e.calculated || 0) * (e.price || 0);
    });
    
    return total;
  }, [products, spec, totalArea, materials]);

  const labourTotal = useMemo(() => {
    let total = 0;
    const hasMembrane = spec.membraneType === 'single_ply' 
      ? spec.deckMembraneId 
      : (spec.deckUnderlayId && spec.deckCapId);
    const isMechFix = spec.membraneFixingMethod === 'mech_fix';
    
    if (totalArea > 0 && hasMembrane) {
      total += totalArea * (labour.find(l => l.id === 'membrane')?.rate ?? 
               DEFAULT_LABOUR_RATES[isMechFix ? 'membrane_mech' : 'membrane_adhered']);
    }
    
    if (totalArea > 0 && spec.vclId) {
      total += totalArea * (labour.find(l => l.id === 'vcl')?.rate ?? DEFAULT_LABOUR_RATES.vcl);
    }
    
    if (totalArea > 0 && spec.insulationId) {
      const layers = spec.insulationLayers || 1;
      total += totalArea * layers * (labour.find(l => l.id === 'insulation')?.rate ?? 
               DEFAULT_LABOUR_RATES[layers > 1 ? 'insulation_double' : 'insulation_single']);
    }
    
    // Upstands
    areas.upstands.forEach(u => {
      const band = u.girthBand || 'girth_300';
      total += (u.length || 0) * (labour.find(l => l.id === band)?.rate ?? DEFAULT_LABOUR_RATES[band]);
    });
    
    // Details
    total += (areas.details.outlets || 0) * 
             (labour.find(l => l.id === 'outlets')?.rate ?? DEFAULT_LABOUR_RATES.outlet);
    total += (areas.details.penetrationsSmall || 0) * 
             (labour.find(l => l.id === 'penetrations_small')?.rate ?? DEFAULT_LABOUR_RATES.penetration_small);
    total += (areas.details.penetrationsLarge || 0) * 
             (labour.find(l => l.id === 'penetrations_large')?.rate ?? DEFAULT_LABOUR_RATES.penetration_large);
    total += (areas.details.rooflights || 0) * 
             (labour.find(l => l.id === 'rooflights')?.rate ?? DEFAULT_LABOUR_RATES.rooflight);
    
    return total;
  }, [totalArea, spec, areas, labour]);

  const grandTotal = useMemo(() => {
    const subtotal1 = materialsTotal + labourTotal;
    const overhead = subtotal1 * (summary.overheadPercent || 0) / 100;
    const subtotal2 = subtotal1 + overhead;
    const profit = subtotal2 * (summary.profitPercent || 0) / 100;
    return subtotal2 + profit;
  }, [materialsTotal, labourTotal, summary]);

  // Save function - returns promise for proper await
  const saveArea = useCallback(async () => {
    if (!projectId || !areaId) return;
    
    const areaData = { spec, areas, materials, labour, summary, runCalcData };
    const dataString = JSON.stringify(areaData);
    
    if (dataString === lastSavedDataRef.current) {
      if (isMountedRef.current) setSaveStatus('saved');
      return;
    }
    
    // Store pending save data for beforeunload
    pendingSaveRef.current = {
      spec_data: spec,
      areas_data: areas,
      materials_data: materials,
      labour_data: labour,
      summary_data: summary,
      run_calc_data: runCalcData,
      total_area_m2: totalArea,
      materials_total: materialsTotal,
      labour_total: labourTotal,
      grand_total: grandTotal,
      spec_summary: specSummary
    };
    
    try {
      if (isMountedRef.current) setSaveStatus('saving');
      await api.updateArea(projectId, areaId, pendingSaveRef.current);
      lastSavedDataRef.current = dataString;
      pendingSaveRef.current = null;
      if (isMountedRef.current) setSaveStatus('saved');
    } catch (err) {
      console.error('Failed to save area:', err);
      if (isMountedRef.current) setSaveStatus('error');
    }
  }, [projectId, areaId, spec, areas, materials, labour, summary, runCalcData, totalArea, materialsTotal, labourTotal, grandTotal, specSummary]);

  // Debounced auto-save
  useEffect(() => {
    if (!projectId || !areaId || loading) return;
    
    setSaveStatus('unsaved');
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveArea();
    }, 3000);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [spec, areas, materials, labour, summary, runCalcData, projectId, areaId, loading, saveArea]);

  // Handle unmount and page close
  useEffect(() => {
    isMountedRef.current = true;
    
    // Warn user if there are unsaved changes when closing/refreshing
    const handleBeforeUnload = (e) => {
      if (pendingSaveRef.current) {
        e.preventDefault();
        e.returnValue = '';
        // Try to save synchronously via sendBeacon (fire and forget)
        const url = `${import.meta.env.VITE_API_URL || ''}/projects/${projectId}/areas/${areaId}`;
        navigator.sendBeacon(url, JSON.stringify(pendingSaveRef.current));
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      isMountedRef.current = false;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Fire final save - won't update state since unmounted
      if (pendingSaveRef.current && projectId && areaId) {
        api.updateArea(projectId, areaId, pendingSaveRef.current).catch(console.error);
      }
    };
  }, [projectId, areaId]);

  const renderTab = () => {
    switch (activeTab) {
      case 'spec':
        return (
          <SpecTab
            data={spec}
            onChange={setSpec}
            manufacturers={manufacturers}
            products={products}
            loading={loading}
            onNext={() => setActiveTab('areas')}
          />
        );
      case 'areas':
        return (
          <AreasTab
            data={areas}
            onChange={setAreas}
            spec={spec}
            onSpecChange={setSpec}
            onNext={() => setActiveTab('materials')}
          />
        );
      case 'materials':
        return (
          <MaterialsTab
            areas={areas}
            spec={spec}
            materials={materials}
            onChange={setMaterials}
            products={products}
            runCalcData={runCalcData}
            onRunCalcChange={setRunCalcData}
            onNext={() => setActiveTab('labour')}
          />
        );
      case 'labour':
        return (
          <LabourTab
            areas={areas}
            spec={spec}
            labour={labour}
            onChange={setLabour}
            onNext={() => setActiveTab('summary')}
          />
        );
      case 'summary':
        return (
          <SummaryTab
            materialsTotal={materialsTotal}
            labourTotal={labourTotal}
            summary={summary}
            onChange={setSummary}
            totalArea={totalArea}
            projectId={projectId}
          />
        );
      default:
        return null;
    }
  };

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500 rounded-lg p-6 text-center">
        <p className="text-red-400">{error}</p>
        <Button variant="secondary" onClick={() => window.location.reload()} className="mt-4">
          Refresh Page
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header with back link */}
      <Link 
        to={`/project/${projectId}`} 
        className="text-slate-400 hover:text-white text-sm flex items-center gap-2 mb-4"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to {project?.name || 'Project'}
      </Link>
      
      {/* Area Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">{area?.name || 'Loading...'}</h1>
          <p className="text-slate-400 text-sm">
            {project?.name} {project?.client_name && `• ${project.client_name}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Save Status */}
          <div className={`flex items-center gap-2 text-sm ${
            saveStatus === 'saved' ? 'text-green-400' :
            saveStatus === 'saving' ? 'text-amber-400' :
            saveStatus === 'error' ? 'text-red-400' :
            'text-slate-400'
          }`}>
            {saveStatus === 'saving' && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
            )}
            {saveStatus === 'saved' && (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {saveStatus === 'error' && (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span className="capitalize">
              {saveStatus === 'unsaved' ? 'Unsaved changes' : saveStatus}
            </span>
          </div>
        </div>
      </div>
      
      <TabBar tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
      {renderTab()}
    </div>
  );
};

export default FlatCalc;
